import { isEmpty } from "lodash";

import {
    INVALID_ADVERTISER_TEXT,
    INVALID_MARKETPLACE_TEXT,
    INVALID_SCOPE_TEXT,
} from "../config/insights.config";
import { SfObjectType } from "../store/insightLinkage";
import { salesforceStore } from "../store/salesforce";
import { ACCOUNT_AGNOSTIC_IDS } from "../types/common";

import { getMarketplaceIdFromCountryCode } from "./acsClient.utils";
import {
    validateNumeric,
    validateAlphaNumeric,
    validateLength,
} from "./validation.utils";
import {
    getInsightIdFromSearchParams,
    getValueFromSearchParams,
} from "./window.utils";

export enum QueryParam {
    SALESFORCE_ID = "sfId",
    SALESFORCE_NAME = "sfName",
    MARKETPLACE_ID = "marketplaceId",
    OPPORTUNITY_ID = "opportunityId",
    FILTERS = "filters",
    UNIFIED_FILTERS = "unifiedFilters",
    IDENTIFIER = "identifier",
    SALESFORCE_OBJECT_TYPE = "sfObjectType",
    SALESFORCE_OBJECT_ID = "sfObjectId",
    SPOOF_AS_USER = "spoofAsUser",
}

export enum IHubQueryParam {
    INITIAL_TAB = "initialTab",
    YOUR_INSIGHT_CONTENT_TYPE = "yourInsightContentType",
    YOUR_INSIGHT_SUB_TYPE = "yourInsightSubType",
}

export enum YourInsightsContentTypeParam {
    INSIGHTS = "insights",
    PITCHED_CONTENT = "pitched-content",
}

export enum YourInsightsSubTypeParam {
    WORKBENCH = "wb",
    PACKAGED = "pkg",
}

export const getAdvertiserId = (): string | undefined => {
    let advertiserId = getValueFromSearchParams(
        QueryParam.SALESFORCE_ID,
    )?.trim();
    if (!advertiserId) {
        advertiserId = getInsightIdFromSearchParams()
            ? ACCOUNT_AGNOSTIC_IDS.WITH_IDENTIFIER
            : ACCOUNT_AGNOSTIC_IDS.NO_IDENTIFIER;
    }
    return advertiserId;
};

export const getSpoofAsUser = (): string | undefined => {
    return getValueFromSearchParams(QueryParam.SPOOF_AS_USER);
};

export const getSfObjectType = (): SfObjectType | undefined => {
    const sfObjectType = getValueFromSearchParams(
        QueryParam.SALESFORCE_OBJECT_TYPE,
    );
    return sfObjectType ? SfObjectType[sfObjectType] : undefined;
};

export const getSfObjectId = (): string | undefined => {
    return getValueFromSearchParams(QueryParam.SALESFORCE_OBJECT_ID);
};

export const getAdvertiserName = (): string | undefined => {
    const advertiserName = getValueFromSearchParams(QueryParam.SALESFORCE_NAME);
    return advertiserName !== undefined
        ? decodeURI(advertiserName)
        : isAccountAgnosticId()
          ? undefined
          : getAdvertiserId();
};

export const getMarketplaceId = (): string | undefined => {
    const marketplaceId = getValueFromSearchParams(QueryParam.MARKETPLACE_ID);
    if (marketplaceId) {
        return marketplaceId;
    }
    return getMarketplaceIdFromCountryCode(
        salesforceStore.getState().sfContext.locale || "",
    );
};

export const getOpportunityId = (): string | undefined => {
    return getValueFromSearchParams(QueryParam.OPPORTUNITY_ID);
};

export const isAccountAgnosticId = (
    advertiserId = getAdvertiserId(),
): boolean => {
    return Object.values(ACCOUNT_AGNOSTIC_IDS).includes(
        advertiserId as ACCOUNT_AGNOSTIC_IDS,
    );
};

export const getNormalizedAdvertiserId = (): string | undefined => {
    const advertiserId = getAdvertiserId();
    return isAccountAgnosticId(advertiserId)
        ? ACCOUNT_AGNOSTIC_IDS.NO_IDENTIFIER
        : advertiserId;
};

export const isScopedToAccount = (): boolean =>
    getAdvertiserId() !== undefined &&
    getMarketplaceId() !== undefined &&
    !isAccountAgnosticId();

export const isValidAccount = (): boolean =>
    isValidAdvertiserId() && isValidMarketplaceId();

export const isValidAdvertiserId = (): boolean => {
    const advertiserId = getAdvertiserId();
    return (
        advertiserId !== undefined &&
        !isAccountAgnosticId() &&
        validateAlphaNumeric(advertiserId) &&
        validateLength(advertiserId, 18)
    );
};

export const hasSfObjectContext = (): boolean =>
    !isEmpty(getAdvertiserId()) &&
    !isEmpty(getSfObjectType()) &&
    !isEmpty(getSfObjectId());

export const isValidMarketplaceId = (): boolean => {
    const marketplaceId = getMarketplaceId();
    return marketplaceId !== undefined && validateNumeric(marketplaceId);
};

export const getInvalidScopeMessage = (): string => {
    const advertiserId = getAdvertiserId();
    const marketplaceId = getMarketplaceId() ?? "undefined";
    const invalidTextList: string[] = [];

    if (advertiserId && marketplaceId && !isAccountAgnosticId()) {
        if (!isValidAdvertiserId()) {
            invalidTextList.push(INVALID_ADVERTISER_TEXT(advertiserId));
        }

        if (!isValidMarketplaceId()) {
            invalidTextList.push(INVALID_MARKETPLACE_TEXT(marketplaceId));
        }
    } else {
        invalidTextList.push(INVALID_SCOPE_TEXT);
    }

    return invalidTextList.join(", ");
};
